import { useEffect } from 'react';
import { useChat, selectActiveConversation } from '../store';
import { api } from '../api';
import { overrideStyle, useThemeOverride } from '../utils/chatThemeOverrides';
import { ChatHeader } from './ChatHeader';
import { MessageList } from './MessageList';
import { MessageInput } from './MessageInput';
import { PinnedStrip } from './PinnedStrip';
import { TypingIndicator } from './TypingIndicator';
import type { ActiveCallInfo } from './CallModal';

interface ChatPanelProps {
  onStartCall: (info: ActiveCallInfo) => void;
  onOpenInfo: () => void;
}

export function ChatPanel({ onStartCall, onOpenInfo }: ChatPanelProps) {
  const conversation = useChat(selectActiveConversation);
  const messagesByConv = useChat((s) => s.messagesByConv);
  const setMessages = useChat((s) => s.setMessages);
  const setStarredIds = useChat((s) => s.setStarredIds);
  // Per-conversation theme override — when set, takes precedence over
  // the global ChatPrefs (utils/chatPrefs) by setting CSS custom
  // properties inline on the panel. Empty override yields no inline
  // vars so the global root values bleed through unchanged.
  const themeOverride = useThemeOverride(conversation?.id ?? '');

  // Lazy-load message history the first time a conversation is selected.
  useEffect(() => {
    if (!conversation) return;
    if (messagesByConv[conversation.id]) return;
    void (async () => {
      try {
        const res = await api.messages(conversation.id);
        setMessages(conversation.id, res.messages, res.hasMore);
      } catch {
        /* swallow */
      }
    })();
  }, [conversation, messagesByConv, setMessages]);

  // Hydrate starred-message IDs for the active conversation so message bubbles
  // can render their starred state immediately.
  useEffect(() => {
    if (!conversation) return;
    void (async () => {
      try {
        const res = await api.conversationStarred(conversation.id);
        setStarredIds(res.messages.map((m) => m.id));
      } catch {
        /* ignore */
      }
    })();
  }, [conversation, setStarredIds]);

  if (!conversation) {
    return (
      <main className="flex-1 flex items-center justify-center px-6 relative overflow-hidden">
        {/* Subtle decorative orb — same palette as the global mesh. */}
        <div
          aria-hidden
          className="absolute -top-40 -right-40 w-[36rem] h-[36rem] rounded-full bg-indigo-500/[0.05] blur-3xl pointer-events-none"
        />
        <div
          aria-hidden
          className="absolute -bottom-40 -left-40 w-[36rem] h-[36rem] rounded-full bg-emerald-500/[0.04] blur-3xl pointer-events-none"
        />
        <div className="relative text-center max-w-md animate-fade-up">
          <span className="inline-block rounded-full hairline px-3 py-1 text-[10px] font-medium uppercase tracking-[0.2em] text-zinc-400 mb-6 bg-zinc-900/40">
            PGOC Chat
          </span>
          <h2 className="text-4xl font-semibold tracking-tightest text-zinc-50 leading-[1.05] mb-3">
            Conversations,
            <br />
            <span className="text-zinc-500">on the record.</span>
          </h2>
          <p className="text-sm text-zinc-400 leading-relaxed mt-4 max-w-sm mx-auto">
            Pick a conversation from the sidebar, or start a new one. Messages,
            calls, and files stay encrypted at rest inside PGOC infrastructure.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="flex-1 flex flex-col min-w-0" style={overrideStyle(themeOverride)}>
      <ChatHeader
        conversation={conversation}
        onStartCall={onStartCall}
        onOpenInfo={onOpenInfo}
      />
      <PinnedStrip conversationId={conversation.id} />
      <MessageList conversationId={conversation.id} />
      <TypingIndicator conversationId={conversation.id} />
      <MessageInput conversationId={conversation.id} />
    </main>
  );
}
